<template>
    <div v-if="calendarData">
        <div class="flex items-center justify-between flex-col md:flex-row mb-5 md:mb-0">
            <h2
                class="font-heading text-2xl md:text-3xl lg:text-4xl mb-5 text-center uppercase"
                v-text="snippets.calendar_snippets_title"
            ></h2>
            <div class="flex items-center space-x-5">
                <label class="flex items-center space-x-2">
                    <input
                        v-model="workFilter"
                        type="checkbox"
                        class="border-gray-300 text-teal shadow-sm focus:border-teal focus:ring focus:ring-offset-0 focus:ring-teal focus:ring-opacity-50"
                    />
                    <span v-text="snippets.calendar_snippets_filter_work"></span>
                </label>
                <label class="flex items-center space-x-2">
                    <input
                        v-model="personalFilter"
                        type="checkbox"
                        class="border-gray-300 text-orange shadow-sm focus:border-orange-300 focus:ring focus:ring-offset-0 focus:ring-orange focus:ring-opacity-50"
                    />
                    <span v-text="snippets.calendar_snippets_filter_personal"></span>
                </label>
            </div>
        </div>
        <div
            class="transition duration-200 ease-in-out"
            :class="{
                'top-calendar--small transition ease-in-out duration-200 cursor-pointer overflow-hidden relative opacity-90 hover:opacity-100':
                    isCollapsed,
            }"
            @click="isCollapsed = false"
        >
            <div class="flex relative transition ease-in-out duration-200">
                <div class="w-64 inset-y-0 text-gray-500">
                    <div class="h-32 border-t border-gray-400 p-3">08:00</div>
                    <div class="h-32 border-t border-gray-400 p-3">10:00</div>
                    <div class="h-32 border-t border-gray-400 p-3">12:00</div>
                    <div class="h-32 border-t border-gray-400 p-3">14:00</div>
                    <div class="h-32 border-t border-gray-400 p-3">16:00</div>
                    <div class="h-32 border-t border-gray-400 p-3">18:00</div>
                    <div class="h-32 border-t border-gray-400 p-3">20:00</div>
                    <div class="h-32 border-t border-gray-400 p-3">22:00</div>
                </div>
                <div
                    class="z-20 absolute inset-y-0 right-0 w-20 cursor-pointer"
                    style="background: linear-gradient(to left, white 0%, rgba(255, 255, 255, 0) 100%)"
                ></div>
                <swiper
                    :loop="true"
                    :navigation="{
                        prevEl: '.slider-prev',
                        nextEl: '.slider-next',
                    }"
                    :slides-per-view="1"
                    :breakpoints="{
                        '640': {
                            slidesPerView: 3,
                        },
                        '768': {
                            slidesPerView: 4,
                        },
                        '1024': {
                            slidesPerView: 5,
                        },
                        '1280': {
                            slidesPerView: 6,
                        },
                    }"
                    class="flex flex-col"
                    data-slide-name="ACTIVITY_CALENDAR"
                    @swiper="trackSwiperEvents"
                >
                    <swiper-slide v-for="(day, dayIndex) in daysOfTheWeek" :key="dayIndex">
                        <div style="height: 997px">
                            <div
                                v-for="(activityGroup, activityGroupIndex) in day"
                                :key="activityGroupIndex"
                                class="h-32 border-t border-gray-400"
                            >
                                <section
                                    v-for="(activity, activityIndex) in activityGroup.activity"
                                    :key="activityIndex"
                                >
                                    <div
                                        v-show="activeActivityTypeFilters.includes(activity.type.value)"
                                        class="mb-3 w-11/12 p-3 relative z-10"
                                        :class="[
                                            `top-calendar-activity-duration--${activity.duration.value}`,
                                            `job-calendar__item-activity--${activity.type.value}`,
                                            activity.activity_description ? `job-calendar__item-activity-info` : null,
                                        ]"
                                    >
                                        <div v-if="activity.activity_description" class="flex justify-between">
                                            <span v-text="activity.name"></span>
                                            <Popover class="relative">
                                                <PopoverButton
                                                    ><i
                                                        class="fa-solid fa-circle-info opacity-20 text-black top-0 right-2 cursor-pointer"
                                                    ></i
                                                ></PopoverButton>
                                                <PopoverPanel
                                                    class="absolute z-10 bg-white px-5 py-3 right-0 max-w-max"
                                                >
                                                    <p class="text-black" v-text="activity.activity_description"></p>
                                                </PopoverPanel>
                                            </Popover>
                                        </div>
                                        <span v-else v-text="activity.name"></span>

                                        <span
                                            class="w-full text-xs absolute right-3 top-3 hidden"
                                            v-text="activity.time"
                                        ></span>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </swiper-slide>
                    <div class="flex justify-center pt-5 space-x-3">
                        <button class="slider-prev pt-10 hover:text-orange">
                            <i class="fa-regular fa-long-arrow-left text-2xl"></i>
                        </button>
                        <button class="slider-next pt-10 hover:text-orange">
                            <i class="fa-regular fa-long-arrow-right text-2xl"></i>
                        </button>
                    </div>
                </swiper>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import SwiperCore, { Navigation } from "swiper";
import { computed, ref, inject } from "vue";
import { trackSwiperEvents } from "@/analyticsHelper";

const snippets = inject("statamic_snippets");

SwiperCore.use([Navigation]);

const props = defineProps({
    calendarData: {
        type: Object,
        required: true,
    },
});

const daysOfTheWeek = computed(() => [
    props.calendarData.monday,
    props.calendarData.tuesday,
    props.calendarData.wednesday,
    props.calendarData.thursday,
    props.calendarData.friday,
    props.calendarData.saturday,
    props.calendarData.sunday,
]);

const workFilter = ref(true);
const personalFilter = ref(true);
const activeActivityTypeFilters = computed(() => {
    const filters = ["empty"];

    if (workFilter.value) {
        filters.push("work");
    }

    if (personalFilter.value) {
        filters.push("personal");
    }

    return filters;
});

const isCollapsed = ref(true);
</script>

<style lang="css">
.job-calendar__item-activity--personal {
    background-color: #ef7d0a;
    color: #ffffff;
}

.job-calendar__item-activity--work {
    background-color: #2db095;
    color: #ffffff;
}

.top-calendar--small {
    height: 500px;
}

.top-calendar--small:after {
    content: "";
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    height: 200px;
    position: absolute;
    background: -webkit-gradient(linear, left bottom, left top, from(white), to(rgba(255, 255, 255, 0)));
    background: linear-gradient(to top, white 0%, rgba(255, 255, 255, 0) 100%);
}
.top-calendar-activity-duration--empty {
    display: none;
}
.top-calendar-activity-duration--0.5 {
    height: 2rem;
}

.top-calendar-activity-duration--1 {
    height: 4rem;
}

.top-calendar-activity-duration--15 {
    height: 6rem;
}

.top-calendar-activity-duration--2 {
    height: 8rem;
}

.top-calendar-activity-duration--25 {
    height: 10rem;
}

.top-calendar-activity-duration--3 {
    height: 12rem;
}

.top-calendar-activity-duration--35 {
    height: 14rem;
}

.top-calendar-activity-duration--4 {
    height: 16rem;
}

.top-calendar-activity-duration--45 {
    height: 18rem;
}

.top-calendar-activity-duration--5 {
    height: 20rem;
}

.top-calendar-activity-duration--55 {
    height: 22rem;
}

.top-calendar-activity-duration--6 {
    height: 24rem;
}

.top-calendar-activity-duration--65 {
    height: 26rem;
}

.top-calendar-activity-duration--7 {
    height: 28rem;
}

.top-calendar-activity-duration--75 {
    height: 30rem;
}
</style>
